import { useEffect, useMemo, useState } from "react";
import { ReportBackButton } from "../ledgerReports/ReportBackButton";
import { useDispatch, useSelector } from "react-redux";
import { Box, Button, CircularProgress } from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import GridOnIcon from "@mui/icons-material/GridOn";
import moment from "moment";
import {
  globalStyle,
  handleVisible,
  handleDangerVisible,
} from "../../global/Utils";
import { SearchableSelect } from "../Common/SearchableSelect";
import { getItem } from "../../Actions/ItemAction";
import { getUserList } from "../../Actions/UserAction";
import {
  getStockMovements,
  clearStockMovements,
  exportStockLedger,
} from "../../Actions/StockAction";
import {
  MOVEMENT_TYPE_LABEL,
  formatQty,
  isStockTracked,
  stockChipStyle,
  unitOf,
} from "../../global/stock";

/**
 * The stock ledger for one product over a window of time.
 *
 * Stock is held as append-only movements rather than a column, so this is the
 * authoritative view of it: every change reads back with its date, its sign and
 * the document that caused it, and
 *
 *     opening balance + the rows below = closing balance
 *
 * always holds. The quantity an item reports is that sum, never a figure kept
 * separately, which is why this replaced the old stock report as the place to
 * look: the report could only ever show today.
 *
 * Admin and customer read it differently, the same way they do everywhere else:
 * a customer sees their own products and nothing else, while an admin picks a
 * user first and the product dropdown follows that choice. The server enforces
 * the same rule - a customer's request for someone else's item reads as missing
 * - so the user dropdown is the admin's convenience, not the boundary.
 */

const today = () => new Date().toISOString().split("T")[0];

// A month back is the window most people want first, and it keeps the page from
// opening onto the entire history of a long-lived product.
const monthAgo = () =>
  moment().subtract(1, "month").format("YYYY-MM-DD");

// A reading view, not a paged list - one request covers any sane window.
const PAGE_SIZE = 200;

const Figure = ({ label, value, unit, tone }) => (
  <Box
    sx={{
      px: 2,
      py: 1.25,
      borderRadius: 2,
      border: "1px solid #E0E0E0",
      backgroundColor: "#FAFAFA",
      minWidth: 150,
    }}
  >
    <Box sx={{ fontSize: 12, color: "#666" }}>{label}</Box>
    <Box
      sx={{
        fontSize: 18,
        fontWeight: 700,
        color: tone || globalStyle.bgColorBlue,
      }}
    >
      {formatQty(value, unit)}
    </Box>
  </Box>
);

const StockLedger = () => {
  const dispatch = useDispatch();

  const itemList = useSelector((state) => state.ItemListReducer?.itemList || []);
  const { userList, isGettingUserList } = useSelector(
    (state) => state.UserReducer,
  );
  const { userDetails } = useSelector((state) => state?.HeaderReducer);
  const isAdmin = userDetails?.role === "admin";

  const {
    stockMovements,
    movementsItem,
    movementsOpeningBalance,
    movementsClosingBalance,
    movementsTotalResults,
    isGettingStockMovements,
    isExportingStockLedger,
    stockMovementsError,
  } = useSelector((state) => state.StockReducer);

  const [userId, setUserId] = useState("");
  const [itemId, setItemId] = useState("");
  const [startDate, setStartDate] = useState(monthAgo);
  const [endDate, setEndDate] = useState(today);
  const [dateError, setDateError] = useState("");

  useEffect(() => {
    // Empty page/limit means "the whole list" (listPaging in Utils.js) - the
    // dropdown filters in the browser.
    dispatch(getItem("", "", ""));
  }, [dispatch]);

  useEffect(() => {
    // Only an admin can choose whose ledger to read, so only an admin needs
    // the list of users to choose from.
    if (isAdmin) {
      dispatch(getUserList("", "", "", ""));
    }
  }, [dispatch, isAdmin]);

  /**
   * Nothing carries stock but a stock-tracked product, so nothing else belongs
   * in a dropdown whose whole purpose is a stock ledger.
   *
   * A customer's item list already comes back scoped to them, so there is
   * nothing further to narrow; an admin's holds every user's items, and is
   * narrowed to the user they picked.
   */
  const products = useMemo(
    () =>
      itemList.filter(
        (item) =>
          isStockTracked(item) &&
          (!isAdmin || item?.createdBy?._id === userId),
      ),
    [itemList, isAdmin, userId],
  );

  useEffect(() => {
    if (!itemId) {
      dispatch(clearStockMovements());
      return;
    }
    if (startDate && endDate && startDate > endDate) {
      return;
    }
    dispatch(getStockMovements(itemId, 0, PAGE_SIZE, { startDate, endDate }));
  }, [dispatch, itemId, startDate, endDate]);

  useEffect(() => {
    setDateError(
      startDate && endDate && startDate > endDate
        ? "The start date cannot be after the end date."
        : "",
    );
  }, [startDate, endDate]);

  const unit = movementsItem?.unit || unitOf(movementsItem) || "";
  const state = movementsItem?.stockState;
  const chip = state ? stockChipStyle(state) : null;

  // What the window itself moved, as opposed to where the item stands. Taken
  // from the balances rather than by summing the rows, so it stays right even
  // if the window holds more rows than one page.
  const netChange = movementsClosingBalance - movementsOpeningBalance;

  const handleUserChange = (value) => {
    setUserId(value);
    // The product belonged to the user just switched away from, so it goes
    // with them rather than leaving a ledger on screen under the wrong name.
    setItemId("");
  };

  /**
   * Prints what is on screen.
   *
   * Only reachable once a ledger has been built, because a period and a product
   * are exactly what the document is of - there is nothing to print before that.
   */
  const printLedger = (type) => {
    dispatch(
      exportStockLedger(
        itemId,
        { startDate, endDate },
        type,
        {
          itemName: movementsItem?.name || "",
          unit,
          userEmail: isAdmin
            ? userList?.find((user) => user._id === userId)?.email || ""
            : userDetails?.email || "",
          startDate,
          endDate,
        },
        handleVisible,
        handleDangerVisible,
      ),
    );
  };

  const resetRange = () => {
    setStartDate(monthAgo());
    setEndDate(today());
  };

  return (
    <Box className="p-4">
      <ReportBackButton />
      <Box sx={{ mb: 2 }}>
        <Box
          component="h1"
          sx={{
            fontFamily: globalStyle.fontFamilyHeading,
            fontWeight: 700,
            color: globalStyle.bgColorBlue,
            fontSize: {
              xs: globalStyle.headingFontSizeSmall,
              sm: globalStyle.headingFontSize,
            },
            lineHeight: 1.2,
          }}
        >
          Stock Ledger
        </Box>
        <Box
          component="p"
          sx={{
            mt: 0.5,
            fontFamily: globalStyle.fontFamilyParagraph,
            fontSize: { xs: 12, sm: 14 },
            color: "#666",
          }}
        >
          Every movement of one product over a period, with the balance either
          side of it.
        </Box>
      </Box>

      {/* ---- filters ---- */}
      <Box
        sx={{
          mb: 3,
          p: 2,
          borderRadius: 2,
          backgroundColor: "#fff",
          boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
        }}
      >
        {isAdmin &&
          (isGettingUserList ? (
            <div className="flex justify-center items-center h-20">
              <CircularProgress size={26} />
            </div>
          ) : (
            <div className="mb-4">
              <label className="block mb-1 font-medium">
                Select User<span className="text-red-500">*</span>
              </label>
              <SearchableSelect
                name="userId"
                value={userId}
                onChange={(e) => handleUserChange(e.target.value)}
                disabled={!userList || userList.length === 0}
                className="w-full px-4 py-2 border border-gray-300 rounded-md"
              >
                {!userList || userList.length === 0 ? (
                  <option value="">No Users Available</option>
                ) : (
                  <>
                    <option value="">SELECT</option>
                    {userList.map((user) => (
                      <option key={user._id} value={user._id}>
                        {user.email}
                      </option>
                    ))}
                  </>
                )}
              </SearchableSelect>
            </div>
          ))}

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <div className="md:col-span-2">
            <label className="block mb-1 font-medium">Product</label>
            <SearchableSelect
              name="itemId"
              value={itemId}
              onChange={(e) => setItemId(e.target.value)}
              // An admin has chosen nobody yet, so there is nothing to list.
              disabled={isAdmin && !userId}
              className="uppercase w-full px-4 py-2 border border-gray-300 rounded-md"
            >
              <option value="">
                {isAdmin && !userId ? "Select a user first" : "Select a product"}
              </option>
              {products.map((product) => (
                <option key={product._id} value={product._id}>
                  {product.name}
                  {" — stock "}
                  {formatQty(product.currentStock, unitOf(product))}
                </option>
              ))}
            </SearchableSelect>
            {isAdmin && userId && products.length === 0 && (
              <p className="text-sm text-gray-500 mt-1">
                This user has no stock-tracked products.
              </p>
            )}
          </div>

          <div>
            <label className="block mb-1 font-medium">Start Date</label>
            <input
              type="date"
              value={startDate}
              max={endDate || today()}
              onChange={(e) => setStartDate(e.target.value)}
              className={`w-full px-4 py-2 border rounded-md ${
                dateError ? "border-red-500" : "border-gray-300"
              }`}
            />
          </div>

          <div>
            <label className="block mb-1 font-medium">End Date</label>
            <input
              type="date"
              value={endDate}
              min={startDate || undefined}
              onChange={(e) => setEndDate(e.target.value)}
              className={`w-full px-4 py-2 border rounded-md ${
                dateError ? "border-red-500" : "border-gray-300"
              }`}
            />
          </div>
        </div>

        {dateError && (
          <p className="text-red-500 text-sm mt-2">{dateError}</p>
        )}

        <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button size="small" onClick={resetRange}>
            Last month
          </Button>
          <Button
            size="small"
            onClick={() => {
              setStartDate("");
              setEndDate("");
            }}
          >
            All time
          </Button>
        </Box>
      </Box>

      {/* ---- the ledger ---- */}
      {isAdmin && !userId ? (
        <Box py={6} textAlign="center" color="text.secondary">
          Select a user, then one of their products, to see its ledger.
        </Box>
      ) : !itemId ? (
        <Box py={6} textAlign="center" color="text.secondary">
          Select a product to see its ledger.
        </Box>
      ) : isGettingStockMovements ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress size={34} />
        </Box>
      ) : stockMovementsError ? (
        <Box py={4} textAlign="center" color="error.main">
          {stockMovementsError}
        </Box>
      ) : (
        <>
          <Box
            sx={{
              mb: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
              <Box
                sx={{
                  fontFamily: globalStyle.fontFamilyHeading,
                  fontWeight: 700,
                  fontSize: 18,
                }}
              >
                {movementsItem?.name}
              </Box>
              {chip && (
                <span
                  style={{
                    backgroundColor: chip.bg,
                    color: chip.fg,
                    fontSize: 12,
                    fontWeight: 700,
                    padding: "2px 10px",
                    borderRadius: 10,
                  }}
                >
                  {chip.label}
                </span>
              )}
            </Box>

            <Box
              display="flex"
              gap={1.5}
              flexWrap="wrap"
              alignItems="center"
            >
              {/* The ledger exists now, so there is something to print. */}
              {stockMovements.length > 0 && (
                <Box display="flex" gap={1} flexWrap="wrap">
                  <Button
                    variant="outlined"
                    size="small"
                    color="error"
                    startIcon={<PictureAsPdfIcon />}
                    disabled={isExportingStockLedger}
                    onClick={() => printLedger("PDF")}
                  >
                    PDF
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    color="success"
                    startIcon={<GridOnIcon />}
                    disabled={isExportingStockLedger}
                    onClick={() => printLedger("EXCEL")}
                  >
                    Excel
                  </Button>
                </Box>
              )}
              <Figure
                label="Opening Balance"
                value={movementsOpeningBalance}
                unit={unit}
              />
              <Figure
                label="Net Change"
                value={netChange}
                unit={unit}
                tone={netChange < 0 ? "#C62828" : "#1E7D34"}
              />
              <Figure
                label="Closing Balance"
                value={movementsClosingBalance}
                unit={unit}
              />
            </Box>
          </Box>

          {stockMovements.length === 0 ? (
            <Box py={5} textAlign="center" color="text.secondary">
              No movements in this period. Widen the dates, or receive or sell
              this product to see rows here.
            </Box>
          ) : (
            <Box
              sx={{
                backgroundColor: "#fff",
                borderRadius: 2,
                boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                overflow: "hidden",
              }}
            >
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr
                      style={{
                        background: globalStyle.blueColor,
                        color: "#fff",
                      }}
                    >
                      <th className="text-left px-3 py-2">Date</th>
                      <th className="text-left px-3 py-2">Type</th>
                      <th className="text-right px-3 py-2">Qty</th>
                      <th className="text-left px-3 py-2">Reference</th>
                      <th className="text-right px-3 py-2">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stockMovements.map((movement) => {
                      const positive = movement.quantity >= 0;
                      return (
                        <tr key={movement._id} className="border-b">
                          <td className="px-3 py-2 whitespace-nowrap">
                            {movement.date ||
                              (movement.createdAt
                                ? moment(movement.createdAt).format(
                                    "DD MMM YYYY",
                                  )
                                : "-")}
                          </td>
                          <td className="px-3 py-2">
                            {MOVEMENT_TYPE_LABEL[movement.type] ||
                              movement.type}
                          </td>
                          <td
                            className="px-3 py-2 text-right font-bold"
                            style={{ color: positive ? "#1E7D34" : "#C62828" }}
                          >
                            {positive ? "+" : ""}
                            {formatQty(movement.quantity)}
                          </td>
                          <td className="px-3 py-2">
                            {movement.referenceLabel ||
                              movement.referenceType ||
                              "-"}
                            {movement.note ? (
                              <div className="text-xs text-gray-500 italic">
                                {movement.note}
                              </div>
                            ) : null}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {movement.balanceAfter === null ||
                            movement.balanceAfter === undefined
                              ? "-"
                              : formatQty(movement.balanceAfter)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {movementsTotalResults > stockMovements.length && (
                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    fontSize: 13,
                    color: "#666",
                    borderTop: "1px solid #EEE",
                  }}
                >
                  Showing the {stockMovements.length} most recent of{" "}
                  {movementsTotalResults} movements. Narrow the dates to see the
                  rest.
                </Box>
              )}
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default StockLedger;
